import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Grid2,
  Paper,
  IconButton,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import FormikTextField from "../../../components/form/FormikTextField";
import axiosInstance from "../../../utils/axiosInstance";
import { servicesUrl, serviceTypesUrl } from "../../../utils/urls";
import { useNavigate } from "react-router-dom";
import { Delete, Edit } from "@mui/icons-material";
import { toast } from "react-hot-toast";
import PageHeader from "../../../components/page-layout/PageHeader";
import EmptyListUi from "../../../components/EmptyListUi";
import ServiceForm from "../../../components/pages/services/ServiceForm";
import ServicePrintPreference from "../../../components/pages/services/ServicePrintPreference";
import ServiceRequirementToggle from "../../../components/pages/services/ServiceTypeRequirementToggle";
import ServiceTypeInputChip from "../../../components/pages/services/ServiceTypeInputChip";
import ServiceTypesForm from "../../../components/pages/services/ServiceTypesForm";
import { useDispatch } from "react-redux";
import { hideLoader, showLoader } from "../../../store/loaderSlice";

const AddService = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Print preference toggle state
  const [printPreference, setPrintPreference] = useState(true);

  // Service requirement toggle states - NOW TWO SEPARATE BOOLEANS
  const [isRequired, setIsRequired] = useState(false);
  const [isDefault, setIsDefault] = useState(false);

  // Service type list state
  const [serviceTypes, setServiceTypes] = useState([]);
  // serviceTypes is valid if all name is not empty string
  const isServiceTypesValid = serviceTypes.some((s) => !s.name);

  const addNewServiceType = () => {
    setServiceTypes((prev) => [
      ...prev,
      {
        id: Date.now(),
        name: "",
        file: null,
        preview: null,
      },
    ]);
  };

  // Remove confirmation dialog state
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
  const [removeCardIndex, setRemoveCardIndex] = useState(null);

  // Open remove confirmation dialog
  const handleRemove = (idx) => {
    setRemoveCardIndex(idx);
    setConfirmRemoveOpen(true);
  };

  // Confirm removal of a service type
  const handleConfirmRemove = () => {
    setServiceTypes((prev) => prev.filter((_, i) => i !== removeCardIndex));
    setConfirmRemoveOpen(false);
  };

  // Validation schema with custom messages
  const validationSchema = Yup.object({
    serviceName: Yup.string().required("Service name is required"),
    description: Yup.string().required("Description is required"),
  });

  return (
    <Formik
      initialValues={{ serviceName: "", description: "" }}
      validationSchema={validationSchema}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          dispatch(showLoader("Adding New Service..."));
          // 1) Create the service
          const { data: service } = await axiosInstance.post(servicesUrl, {
            name: values.serviceName,
            description: values.description,
            printPreference,
            isRequired, // Send boolean
            isDefault,  // Send boolean
          });
          toast.success(service.message || "Service created successfully");

          // 2) Upload each icon as multipart/form-data
          const calls = await Promise.all(
            serviceTypes.map(async (type) => {
              const formData = new FormData();
              formData.append("name", type.name);
              formData.append("serviceId", service.id);

              let fileToUpload = type.file;

              // If user didn't choose a file, fetch the default preview URL
              if (!fileToUpload && type.preview) {
                const res = await fetch(type.preview);
                const blob = await res.blob();
                const filename =
                  type.name.toLowerCase().replace(/\s+/g, "-") + ".png";
                fileToUpload = new File([blob], filename, {
                  type: blob.type,
                });
              }

              formData.append("image", fileToUpload);

              return axiosInstance.post(serviceTypesUrl, formData);
            })
          );

          // Success: navigate to services list
          navigate("/services");
        } catch (error) {
          // Show error in console
          console.error(
            "Upload error:",
            error.response ? error.response.data : error
          );
          const errorMsg =
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;

          toast.error(errorMsg || "Failed to create service");
        } finally {
          setSubmitting(false);
          dispatch(hideLoader());
        }
      }}
    >
      {({ handleSubmit, isSubmitting }) => (
        <Form onSubmit={handleSubmit}>
          <Box>
            {/* Page header */}
            <PageHeader
              title="Add Service"
              buttonOnClick="Save Changes"
              rightComponent={
                <Button
                  type="submit"
                  variant="outlined"
                  disabled={isSubmitting || isServiceTypesValid}
                >
                  {isSubmitting ? "Saving…" : "Save Changes"}
                </Button>
              }
              showBack
            />
            {/* Service form */}
            <ServiceForm />

            {/* Print preference and Service requirement toggles - SIDE BY SIDE */}
            {/* Print preference and Service requirement toggles - WITH DIVIDER */}
            <Box sx={{ display: "flex", gap: 6, alignItems: "flex-start", my: 5 }}>
              <ServicePrintPreference
                value={printPreference}
                onChange={setPrintPreference}
              />

              <Box
                sx={{
                  height: 60,
                  width: "1px",
                  bgcolor: "divider",
                  alignSelf: "stretch",
                  my: 1,
                }}
              />

              <ServiceRequirementToggle
                isRequired={isRequired}
                isDefault={isDefault}
                onRequiredChange={setIsRequired}
                onDefaultChange={setIsDefault}
              />
            </Box>

            {/* FIXED: Removed undefined props for AddService */}
            <ServiceTypesForm
              addNewServiceType={addNewServiceType}
              serviceTypes={serviceTypes}
              setServiceTypes={setServiceTypes}
              isDefault={isDefault}
            />

            {/* Confirm Removal Dialog */}
            <Dialog
              open={confirmRemoveOpen}
              onClose={() => setConfirmRemoveOpen(false)}
            >
              <DialogTitle>Confirm Removal</DialogTitle>
              <DialogContent>
                <Typography>
                  Are you sure you want to remove this service type?
                </Typography>
              </DialogContent>
              <DialogActions sx={{ mr: 2, pb: 3 }}>
                <Button
                  onClick={() => setConfirmRemoveOpen(false)}
                  size="small"
                  variant="outlined"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmRemove}
                  size="small"
                  color="error"
                  variant="outlined"
                >
                  Remove
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        </Form>
      )}
    </Formik>
  );
};

export default AddService;
